'use client';

import { useMemo } from 'react';
import { useGetTopProvidersQuery } from 'src/api/redux';
import { getSearchQueryParamsForAPI } from 'src/utils/url.helpers';
import useProfileSelections from './useProfileSelections';
import {
  PROFILE_VOLUME_MIN_YEAR,
  PROFILE_VOLUME_MAX_YEAR,
} from 'src/components/constants';

interface useGetNewTopProvidersProps {
  providerId: string;
  location: string;
}

interface VolumeData {
  [key: string]: any;
}

interface Filter {
  type: string;
  values: string[];
}

// Use Set for O(1) lookup instead of array.find()
// This pattern should be far more performant.
// DRUG codes are excluded as they don't work with Volume By Location (VBL) endpoints
const VALID_CODE_TYPES = new Set([
  'CPT',
  'HCPCS',
  'ICD',
  'ICD10D',
  'ICD10P',
  'ICDP',
  'DRG',
]);

// Maps internal code types to API query parameter names
// Based on backend LEGACY_CODE_TYPE_DICT mapping
const CODE_TYPE_TO_PARAM: Record<string, string> = {
  CPT: 'cpt',
  HCPCS: 'hcpcs',
  ICD: 'icd',
  ICD10D: 'icd', // ICD10D maps to 'icd' parameter
  ICD10P: 'icdp', // ICD10P maps to 'icdp' parameter (not 'icd10p')
  ICDP: 'icdp',
  DRG: 'drg',
};

const useGetNewTopProviders = ({
  providerId,
  location,
}: useGetNewTopProvidersProps) => {
  const { selectedFilterSet, selectedCohort, isAllCodeSets } =
    useProfileSelections(location);

  const queryParams = useMemo(() => {
    let params = '';

    // Mutual exclusion: either selectedFilterSet OR selectedCohort, not both
    if (selectedCohort) {
      // When we have a cohort, we won't have a selectedFilterSet
      params = `?${encodeURIComponent('cohort_id')}=${encodeURIComponent(
        selectedCohort.id
      )}`;
    } else if (isAllCodeSets) {
      params = `?${encodeURIComponent('all_code_sets')}=${encodeURIComponent(
        isAllCodeSets
      )}`;
    } else if (selectedFilterSet) {
      // When we have a selectedFilterSet, we won't have a selectedCohort
      const filters: Filter[] = selectedFilterSet.filters || [];

      const filterValues = filters.reduce(
        (acc: Record<string, string[]>, filter) => {
          const codeTypeUpper = filter.type.toUpperCase();
          // Use Set.has() for efficient lookup with case-insensitive comparison
          if (
            VALID_CODE_TYPES.has(codeTypeUpper) &&
            filter.values?.length > 0
          ) {
            // Map code type to correct API parameter name (e.g., ICD10P → icdp)
            const paramName =
              CODE_TYPE_TO_PARAM[codeTypeUpper] || filter.type.toLowerCase();
            acc[paramName] = filter.values;
          }
          return acc;
        },
        {}
      );

      params = getSearchQueryParamsForAPI(filterValues);
    }

    // Filter every branch to the profile volume year window
    params = getSearchQueryParamsForAPI(
      {
        dateRange: {
          gte: String(PROFILE_VOLUME_MIN_YEAR),
          lte: String(PROFILE_VOLUME_MAX_YEAR),
        },
      },
      params
    );

    return params;
  }, [selectedFilterSet, selectedCohort, isAllCodeSets]);

  const shouldFetch = !!providerId && !!location;

  const { data, error, isLoading, isFetching, refetch } =
    useGetTopProvidersQuery(
      { providerId, queryParams },
      {
        skip: !shouldFetch,
      }
    );

  return {
    data: data as VolumeData | undefined,
    error,
    isLoading,
    isValidating: isFetching,
    mutate: refetch,
  };
};

export default useGetNewTopProviders;
